import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Check, Download, FileJson, FileSpreadsheet, GitMerge, History, Loader2, RefreshCcw, Undo2, Upload } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/Confirm";
import { Pill } from "@/components/Music";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError, downloadFile, formatDateTime } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cn } from "@/lib/utils";

const KINDS = [
  { value: "catalog", label: "Master catalog" },
  { value: "publisher", label: "Publisher tab (catalog + IPI)" },
  { value: "writer", label: "A writer's tab" },
  { value: "production", label: "Production tracker" },
  { value: "skip", label: "Skip this tab" },
];

function Steps({ step }) {
  const items = ["Upload", "Match tabs", "Import"];
  return (
    <section className="panel px-5 py-4 flex items-center gap-3 mb-4">
      {items.map((s, i) => {
        const done = i < step;
        const on = i === step;
        return (
          <div key={s} className="flex items-center gap-2.5 flex-1">
            <span
              className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center font-mono text-[12px] font-semibold shrink-0",
                done ? "bg-stock-in text-white" : on ? "bg-brand text-white" : "bg-card border border-line text-muted2"
              )}
            >
              {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span className={cn("text-[13.5px] font-semibold", !done && !on && "text-muted2")}>{s}</span>
            {i < items.length - 1 && <span className="flex-1 h-px bg-line" />}
          </div>
        );
      })}
    </section>
  );
}

export default function Import() {
  const { user } = useAuth();
  const { refresh } = useSettings();
  const canAdmin = ["owner", "admin"].includes(user?.role);
  const [preview, setPreview] = useState(null);
  const [kinds, setKinds] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [drag, setDrag] = useState(false);
  const [mode, setMode] = useState("merge");
  const [history, setHistory] = useRemembered("imports");
  const confirm = useConfirm();
  const input = useRef(null);

  const loadHistory = useCallback(() => {
    api
      .get("/imports")
      .then(({ data }) => setHistory(data))
      .catch(() => setHistory([]));
  }, [setHistory]);
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const undo = async (h) => {
    const edited = h.edited_since ? ` ${h.edited_since} of them have been edited since — those edits go too.` : "";
    const body =
      h.mode === "replace"
        ? `This removes the ${h.remaining} tracks it brought in and puts back the catalog as it was before this import.${edited}`
        : `This deletes the ${h.remaining} tracks it added and takes back details it filled into other tracks (unless they've been changed since).${edited}`;
    if (!(await confirm({ title: `Undo “${h.filename}”?`, body, confirmText: "Undo import", destructive: true }))) return;
    try {
      const { data } = await api.post(`/imports/${h.batch_id}/undo`);
      toast.success(
        `Undone — ${data.removed} tracks removed` +
          (data.restored ? `, ${data.restored} put back` : "") +
          (data.reverted ? `, ${data.reverted} tracks set back` : "")
      );
      loadHistory();
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) return toast.error("Choose an .xlsx file (Excel or Google Sheets → Download → .xlsx)");
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/preview", fd);
      setPreview(data);
      setKinds(Object.fromEntries(data.tabs.map((t) => [t.name, t.kind])));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (
      mode === "replace" &&
      preview.existing_tracks > 0 &&
      !(await confirm({
        title: `Replace all ${preview.existing_tracks} tracks?`,
        body: "Every track in this workspace is removed first, then this file is brought in. Writers and their logins stay. You can undo this from Import history.",
        confirmText: "Replace everything",
        destructive: true,
      }))
    )
      return;
    setBusy(true);
    try {
      const tabs = Object.fromEntries(Object.entries(kinds).map(([name, kind]) => [name, { kind }]));
      const { data } = await api.post("/import/commit", { job_id: preview.job_id, tabs, mode });
      setResult(data);
      setPreview(null);
      setMode("merge");
      refresh();
      loadHistory();
      toast.success("Imported");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const step = result ? 3 : preview ? 1 : 0;
  const toImport = preview ? preview.tabs.filter((t) => kinds[t.name] !== "skip").reduce((a, t) => a + t.row_count, 0) : 0;

  return (
    <Layout title="Import & export" subtitle="Bring your spreadsheet in, or take the catalog out in the same layout">
      <Steps step={step} />

      {result && (
        <section className="panel panel-pad mb-4">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center shrink-0">
              <Check className="w-5 h-5 text-stock-in" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-[20px] font-semibold">Your spreadsheet is in</h2>
              <p className="text-[13.5px] text-muted2 mt-1">
                {result.mode === "replace" && `Replaced ${result.replaced} tracks. `}
                {result.created} new tracks
                {result.merged ? `, ${result.merged} existing tracks had details filled in` : ""}
                {result.unchanged ? `, ${result.unchanged} rows were already in the catalog and left as they were` : ""}. {result.writers_total} writers on the roster
                {result.writers_created ? ` (${result.writers_created} new)` : ""}. Changed your mind? Undo it from Import history below.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button asChild className="bg-brand hover:bg-brand-hover">
                  <Link to="/overview">
                    See the overview <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/catalog?filter=problems">Review problems</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/writers">Check writers</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {!preview && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            upload(e.dataTransfer.files?.[0]);
          }}
          className={cn("panel border-2 border-dashed p-10 text-center mb-4 transition-colors", drag ? "border-brand bg-brand-light/40" : "border-line")}
        >
          <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center mx-auto">
            {busy ? <Loader2 className="w-5 h-5 text-brand animate-spin" /> : <Upload className="w-5 h-5 text-brand" />}
          </div>
          <h2 className="font-display text-[20px] font-semibold mt-4">{busy ? "Reading your file…" : "Drop your .xlsx here"}</h2>
          <p className="text-[13.5px] text-muted2 mt-1.5 max-w-lg mx-auto">
            Each tab is matched to a place in the app — the master catalog, a writer's own tab (their PRO and IPI are read from the first row) or the production tracker. Nothing is saved
            until you confirm. You'll choose whether to merge it into what's there or replace everything, and every import can be undone.
          </p>
          <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <Button className="mt-5 bg-brand hover:bg-brand-hover" disabled={busy} onClick={() => input.current?.click()}>
            Choose file
          </Button>
        </div>
      )}

      {preview && (
        <div className="grid xl:grid-cols-[1.7fr_1fr] gap-4 items-start mb-4">
          <section className="panel overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-stock-in" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold truncate">{preview.filename}</div>
                <div className="text-[12px] text-muted2">
                  {preview.tabs.length} tabs · {preview.row_total} rows with a track title
                  {preview.existing_tracks ? ` · ${preview.existing_tracks} tracks already in this workspace` : ""}
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="text-[12.5px] font-semibold text-brand">
                Choose another file
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-surface/70 border-y border-line">
                    {["Tab in your file", "Rows", "Becomes", "What we found"].map((h) => (
                      <th key={h} className="t-head text-left px-4 py-2.5 font-medium">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.tabs.map((t) => (
                    <tr key={t.name} className={cn("border-b border-line/70 last:border-0", kinds[t.name] === "skip" && "opacity-60")}>
                      <td className="px-4 py-2.5 text-[13.5px] font-semibold">{t.name}</td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-muted2">{t.row_count}</td>
                      <td className="px-4 py-2.5">
                        <select
                          aria-label={`What ${t.name} becomes`}
                          value={kinds[t.name]}
                          onChange={(e) => setKinds((k) => ({ ...k, [t.name]: e.target.value }))}
                          className="h-9 w-full max-w-[240px] rounded-lg border border-line bg-card px-2.5 text-[13px]"
                        >
                          {KINDS.map((k) => (
                            <option key={k.value} value={k.value}>
                              {k.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2.5 text-[12.5px] text-muted2">
                        {t.header?.name && kinds[t.name] === "writer" ? <span className="text-ink font-medium">{t.header.name}</span> : null}
                        {t.header?.name && kinds[t.name] === "writer" ? " · " : ""}
                        {t.note}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section className="panel p-5 pb-2">
            <div className="flex items-center justify-between mb-2">
              <div className="eyebrow">Clean-up preview</div>
              <span className="text-[12px] text-muted2">Nothing changes until you import</span>
            </div>
            {preview.findings.length === 0 && <p className="text-[13px] text-muted2 py-4">No problems found.</p>}
            {preview.findings.map((f, i) => (
              <div key={i} className="flex gap-3 items-start py-3 border-t border-line">
                <div
                  className={cn(
                    "w-7 h-7 rounded-lg flex items-center justify-center shrink-0",
                    f.kind === "bad" ? "bg-rose-50 dark:bg-rose-950/40 text-stock-out" : f.kind === "good" ? "bg-emerald-50 dark:bg-emerald-950/40 text-stock-in" : "bg-gold-light text-gold"
                  )}
                >
                  {f.kind === "good" ? <Check className="w-3.5 h-3.5" /> : f.kind === "warn" ? <GitMerge className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold">{f.title}</div>
                  <div className="text-[12px] text-muted2 mt-0.5 break-words">{f.body}</div>
                </div>
                <span className={cn("text-[11px] font-semibold whitespace-nowrap", f.kind === "bad" ? "text-stock-out" : f.kind === "good" ? "text-stock-in" : "text-gold")}>{f.action}</span>
              </div>
            ))}
          </section>
          <section className="xl:col-span-2 panel p-4">
            <div className="eyebrow mb-3">How should this file go in?</div>
            <div role="radiogroup" aria-label="Import mode" className="grid md:grid-cols-2 gap-3">
              {[
                {
                  v: "merge",
                  icon: GitMerge,
                  t: "Merge into what's there",
                  b: `Adds new songs and fills in blanks on the ${preview.existing_tracks} tracks you already have. Nothing you've entered is overwritten. Safe to do with an updated copy of the same sheet.`,
                },
                {
                  v: "replace",
                  icon: RefreshCcw,
                  t: "Replace everything",
                  b: "Removes every track in this workspace first, then brings this file in fresh. Writers and logins stay. Can be undone.",
                },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  role="radio"
                  aria-checked={mode === o.v}
                  onClick={() => setMode(o.v)}
                  className={cn("text-left rounded-xl border p-4 flex gap-3 transition-colors", mode === o.v ? (o.v === "replace" ? "border-stock-out bg-rose-50/60 dark:bg-rose-950/30" : "border-brand bg-brand-light/60") : "border-line hover:border-brand-ring")}
                >
                  <span className={cn("w-4 h-4 mt-0.5 rounded-full border-2 shrink-0", mode === o.v ? (o.v === "replace" ? "border-stock-out bg-stock-out" : "border-brand bg-brand") : "border-line")} />
                  <span>
                    <span className="flex items-center gap-2 text-[14px] font-semibold">
                      <o.icon className="w-4 h-4" /> {o.t}
                    </span>
                    <span className="block text-[12.5px] text-muted2 mt-1 leading-relaxed">{o.b}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>
          <div className="xl:col-span-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreview(null)}>
              Back
            </Button>
            <Button className={mode === "replace" ? "bg-stock-out hover:bg-rose-700 text-white" : "bg-brand hover:bg-brand-hover"} disabled={busy || !toImport} onClick={commit}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {mode === "replace" ? `Replace with ${toImport} rows` : `Import ${toImport} rows`}
            </Button>
          </div>
        </div>
      )}

      <section className="panel overflow-hidden mb-4">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-line">
          <History className="w-4 h-4 text-muted2" />
          <div className="eyebrow">Import history</div>
          <span className="text-[12px] text-muted2 ml-auto">Undo takes a file back out</span>
        </div>
        {!history ? (
          <div className="p-5 text-[13px] text-muted2">Loading…</div>
        ) : history.length === 0 ? (
          <div className="p-5 text-[13px] text-muted2">Nothing imported yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="bg-surface/70 border-b border-line">
                  {["File", "Imported", "How", "Added", "Filled in", "Already there", ""].map((h) => (
                    <th key={h} className="t-head text-left px-4 py-2.5 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.batch_id} className={cn("border-b border-line/70 last:border-0", h.undone_at && "opacity-60")}>
                    <td className="px-4 py-3">
                      <div className="text-[13.5px] font-semibold flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-stock-in shrink-0" /> {h.filename}
                      </div>
                      <div className="text-[11.5px] text-muted2 mt-0.5">{h.tabs.length} tabs</div>
                    </td>
                    <td className="px-4 py-3 text-[12.5px]">
                      {formatDateTime(h.created_at)}
                      <div className="text-[11.5px] text-muted2">by {h.created_by}</div>
                    </td>
                    <td className="px-4 py-3">{h.mode === "replace" ? <Pill tone="bad">Replaced {h.counts.replaced}</Pill> : <Pill tone="brand">Merged</Pill>}</td>
                    <td className="px-4 py-3 font-mono text-[13px]">
                      {h.counts.created}
                      {!h.undone_at && h.remaining !== h.counts.created && <div className="text-[11px] text-muted2 font-sans">{h.remaining} still here</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-[13px]">{h.counts.merged}</td>
                    <td className="px-4 py-3 font-mono text-[13px]">{h.counts.unchanged}</td>
                    <td className="px-4 py-3 text-right">
                      {h.undone_at ? (
                        <span className="text-[12px] text-muted2">
                          Undone {formatDateTime(h.undone_at)}
                          {h.undone_by ? ` by ${h.undone_by}` : ""}
                        </span>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => undo(h)} className="text-stock-out">
                          <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Undo
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel panel-pad">
        <div className="eyebrow">Export</div>
        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <button onClick={() => downloadFile("/export/catalog.xlsx", "catalog.xlsx").catch((e) => toast.error(apiError(e)))} className="rounded-xl border border-line p-4 text-left hover:border-brand-ring">
            <FileSpreadsheet className="w-5 h-5 text-stock-in" />
            <div className="text-[14px] font-semibold mt-3">Catalog as Excel</div>
            <div className="text-[12.5px] text-muted2 mt-1">Same column bands as your sheet: Track, Recording, Release, Codes, Publishing, Mechanical.</div>
          </button>
          <button onClick={() => downloadFile("/export/catalog.csv", "catalog.csv").catch((e) => toast.error(apiError(e)))} className="rounded-xl border border-line p-4 text-left hover:border-brand-ring">
            <Download className="w-5 h-5 text-sec-release" />
            <div className="text-[14px] font-semibold mt-3">Catalog as CSV</div>
            <div className="text-[12.5px] text-muted2 mt-1">For Google Sheets or another system.</div>
          </button>
          {canAdmin && (
            <button onClick={() => downloadFile("/backup", "backup.json").catch((e) => toast.error(apiError(e)))} className="rounded-xl border border-line p-4 text-left hover:border-brand-ring">
              <FileJson className="w-5 h-5 text-brand" />
              <div className="text-[14px] font-semibold mt-3">Full backup (JSON)</div>
              <div className="text-[12.5px] text-muted2 mt-1">Every track, writer, split, registration and history entry.</div>
            </button>
          )}
        </div>
      </section>
    </Layout>
  );
}
